import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/lib/api';
import type {
  CreateDateChangeRequestInput,
  DateChangeRequest,
  DateChangeRequestListResponse,
  ResolveDateChangeRequestInput,
} from '@/types';

/**
 * F-S2 date change request hooks.
 *
 * Endpoints (see docs/phase2/new-scope-analysis.md §F-S2):
 *  - POST   /api/projects/{projectId}/date-change-requests          — PM/SA raise a request
 *  - GET    /api/projects/{projectId}/date-change-requests?status=  — pending requests for a project
 *                                                                     (drives the detail-page banner + per-field badge)
 *  - GET    /api/date-change-requests?status=pending                — leadership-only cross-project queue
 *  - POST   /api/projects/{projectId}/date-change-requests/{id}/resolve — leadership approve / reject
 *
 * The list endpoints return a bare array of DB rows; `normalizeDateChangeRequests` maps them.
 */

/**
 * Raw row as returned by packages/projects/handlers/list-date-change-requests.ts: a bare JSON array
 * (not `{ requests }`) with `created_at`, `requested_by` = Cognito sub, `requested_by_email`,
 * `reviewed_by_email`, and — on the global route — `project_title`.
 */
interface DateChangeRequestRow
  extends Omit<DateChangeRequest, 'requested_at' | 'resolved_by'> {
  created_at?: string;
  requested_at?: string;
  requested_by_email?: string | null;
  reviewed_by_email?: string | null;
  resolved_by?: string | null;
}

/** Map the API row onto the UI type. Tolerates the legacy `{ requests }` envelope too. */
export function normalizeDateChangeRequests(
  body: DateChangeRequestRow[] | DateChangeRequestListResponse | null | undefined,
): DateChangeRequest[] {
  const rows: DateChangeRequestRow[] = Array.isArray(body)
    ? body
    : ((body?.requests as DateChangeRequestRow[] | undefined) ?? []);
  return rows.map((r) => ({
    ...r,
    // project_id IS the jira_key in this schema.
    jira_key: r.jira_key ?? r.project_id,
    requested_by: r.requested_by_email ?? r.requested_by,
    requested_at: r.requested_at ?? r.created_at ?? '',
    resolved_by: r.resolved_by ?? r.reviewed_by_email ?? null,
  }));
}

/** Pending date change requests for a single project (banner + per-field "pending" badge). */
export function useProjectDateChangeRequests(projectId: string) {
  const client = useApiClient();

  return useQuery({
    queryKey: ['date-change-requests', 'project', projectId],
    queryFn: async () => {
      const response = await client.get<DateChangeRequestRow[] | DateChangeRequestListResponse>(
        `/api/projects/${projectId}/date-change-requests?status=pending`
      );
      return normalizeDateChangeRequests(response.data);
    },
    enabled: !!projectId,
  });
}

/** Leadership-only cross-project queue of pending requests (dashboard section). */
export function usePendingDateChangeRequests(enabled = true) {
  const client = useApiClient();

  return useQuery({
    queryKey: ['date-change-requests', 'pending'],
    queryFn: async () => {
      const response = await client.get<DateChangeRequestRow[] | DateChangeRequestListResponse>(
        `/api/date-change-requests?status=pending`
      );
      return normalizeDateChangeRequests(response.data);
    },
    enabled,
  });
}

/** PM/SA raise a date change request for a project. */
export function useCreateDateChangeRequest(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateDateChangeRequestInput) => {
      const response = await client.post<DateChangeRequest>(
        `/api/projects/${projectId}/date-change-requests`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['date-change-requests', 'project', projectId],
      });
      void queryClient.invalidateQueries({ queryKey: ['date-change-requests', 'pending'] });
    },
  });
}

/**
 * Leadership approve / reject a pending request. On approve the server applies the date to the
 * project, so we also invalidate the affected project's caches.
 */
export function useResolveDateChangeRequest() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      request,
      input,
    }: {
      request: DateChangeRequest;
      input: ResolveDateChangeRequestInput;
    }) => {
      // The resolve route is project-scoped (date-change-oscar-infra.ts); there is no global one.
      const projectKey = request.jira_key ?? request.project_id;
      const response = await client.post<DateChangeRequest>(
        `/api/projects/${encodeURIComponent(projectKey)}/date-change-requests/${request.id}/resolve`,
        input
      );
      return response.data;
    },
    onSuccess: (_data, { request }) => {
      void queryClient.invalidateQueries({ queryKey: ['date-change-requests', 'pending'] });
      void queryClient.invalidateQueries({
        queryKey: ['date-change-requests', 'project', request.project_id],
      });
      // Approving mutates the project's dates — refresh project detail + lists.
      const projectKey = request.jira_key ?? request.project_id;
      void queryClient.invalidateQueries({ queryKey: ['project', projectKey] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
